import { AnalyticsSummary, TenantSettings, type TokenPrices, UsageSummary } from '@rp/contracts'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useReadySession } from '../../session/useSession'
import { settingsKeys, TENANT_SETTINGS_PATH } from '../settings/queries'

export const TREND_DAYS = 30
export const NEAR_LIMIT_RATIO = 0.8

export const usageKeys = {
  all: ['usage'] as const,
  summary: (tenantId: string) => ['usage', 'summary', tenantId] as const,
  analytics: (tenantId: string, days: number) => ['usage', 'analytics', tenantId, days] as const,
}

export const useUsageSummary = () => {
  const { api, membership } = useReadySession()
  return useQuery({
    queryKey: usageKeys.summary(membership.tenantId),
    queryFn: async () => (await api.request(UsageSummary, { method: 'GET', path: '/v1/usage' })).data,
  })
}

export const useUpdateMonthlyTokenBudget = () => {
  const { api, membership } = useReadySession()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (monthlyTokenBudget: number) =>
      (await api.request(TenantSettings, { method: 'PATCH', path: TENANT_SETTINGS_PATH, body: { monthlyTokenBudget } })).data,
    onSuccess: async (saved) => {
      queryClient.setQueryData(settingsKeys.tenant(membership.tenantId), saved)
      await queryClient.invalidateQueries({ queryKey: usageKeys.summary(membership.tenantId) })
    },
  })
}

export interface TokenPricesChange {
  tokenPrices: TokenPrices
  expectedTokenPrices: TokenPrices
}

export const useUpdateTokenPrices = () => {
  const { api, membership } = useReadySession()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (change: TokenPricesChange) =>
      (await api.request(TenantSettings, { method: 'PATCH', path: TENANT_SETTINGS_PATH, body: change })).data,
    onSuccess: (saved) => queryClient.setQueryData(settingsKeys.tenant(membership.tenantId), saved),
  })
}

export const useUsageTrend = (days: number = TREND_DAYS) => {
  const { api, membership } = useReadySession()
  return useQuery({
    queryKey: usageKeys.analytics(membership.tenantId, days),
    queryFn: async () =>
      (await api.request(AnalyticsSummary, { method: 'GET', path: '/v1/analytics/summary', query: { days } })).data,
  })
}
